import { DEFAULT_CARD_TYPES } from "@constants/bank-funding-profile";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useAuthSession from "@hooks/useAuthSession";
import useFundingProfile from "@hooks/useFundingProfile";
import { useFundingProfileFormConfig } from "@hooks/useFundingProfileFormConfig";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePaymentInfo from "@hooks/usePaymentInfo";
import { Currency } from "@types";
import { parseClabe, parseIfsc } from "@utils/bank-parsing";
import { MemberBankFundingType, MemberSettingFundingMode, MemberSettingNameMode } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FundingProfileFieldValues } from "../types";

export const useBankFundingProfileFormController = () => {
  const { bankFormConfig } = useFundingProfileFormConfig();
  const { fundingProfileList } = useFundingProfile();
  const { getPaymentMethodByKey } = usePaymentInfo();
  const { nameMode, currency, phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError } = useMemberSettings();
  const { legalName } = useAuthSession();

  const checkIfFundingProfileExists = useCallback(
    (accountNo: string, accountType: string): boolean => {
      const foundFundingProfile = fundingProfileList?.find(
        (profile) =>
          profile.accNo === accountNo &&
          profile.type === getPaymentMethodByKey(accountType)?.type &&
          profile.key === accountType,
      );

      return !!foundFundingProfile;
    },
    [fundingProfileList, getPaymentMethodByKey],
  );

  // Shared derivation callbacks to avoid logic duplication across schema and watchers
  const deriveBankType = useCallback((c: Currency, opt?: string, withIfsc?: boolean): MemberBankFundingType => {
    switch (c) {
      case Currency.INR: {
        if (opt === "upi") return MemberBankFundingType.UPI;
        if (opt === "bank" && withIfsc) return MemberBankFundingType.IFSC;
        return MemberBankFundingType.DEFAULT;
      }
      case Currency.MXN:
        return MemberBankFundingType.CLABE;
      case Currency.BRL:
        return MemberBankFundingType.PIX;
      case Currency.PEN:
        return MemberBankFundingType.CCI;
      default:
        return MemberBankFundingType.DEFAULT;
    }
  }, []);

  const deriveAccountType = useCallback(
    (c: Currency, v: Partial<FundingProfileFieldValues>): string | undefined => {
      if (bankFormConfig?.bank?.enabled) return undefined;
      switch (c) {
        case Currency.INR: {
          if (v.bankTypeOption === "upi") return MemberBankFundingType.UPI;
          if (v.bankTypeOption === "bank" && v.ifscChecked) return parseIfsc(v.ifsc)?.key;
          return undefined;
        }
        case Currency.MXN:
          return parseClabe(v.accountNo)?.key;
        case Currency.BRL:
          return MemberBankFundingType.PIX;
        default:
          return undefined;
      }
    },
    [bankFormConfig],
  );

  const formSchema = useMemo(() => {
    const isEnabled = (flag?: boolean) => flag === true;

    const isINR = (currency as Currency) === Currency.INR;
    const isBRL = (currency as Currency) === Currency.BRL;
    const isMXN = (currency as Currency) === Currency.MXN;
    const isPEN = (currency as Currency) === Currency.PEN;
    const isCOP = (currency as Currency) === Currency.COP;

    // Common fields applied to all branches
    let common: z.ZodTypeAny = z.object({
      accountType: ValidatorSchema.payment.bank.bankKey,
      bankType: z.nativeEnum(MemberBankFundingType).optional(),
    });

    // Add name fields if legal name is not fulfilled
    if (!legalName) {
      const nameFields: Record<string, z.ZodTypeAny> = {};
      if (nameMode === MemberSettingNameMode.FullInFirst && isEnabled(bankFormConfig?.fullName?.enabled)) {
        nameFields.accountName = ValidatorSchema.accountName;
      }
      if (nameMode === MemberSettingNameMode.FirstLast) {
        if (isEnabled(bankFormConfig?.firstName?.enabled)) nameFields.firstName = ValidatorSchema.firstName;
        if (isEnabled(bankFormConfig?.lastName?.enabled)) nameFields.lastName = ValidatorSchema.lastName;
      }
      if (Object.keys(nameFields).length) {
        common = (common as z.ZodObject<any>).extend(nameFields) as z.ZodTypeAny;
      }
    }

    // Country/unique fields
    const extraFields: Record<string, z.ZodTypeAny> = {};
    if (isEnabled(bankFormConfig?.pan?.enabled)) {
      extraFields.pan = ValidatorSchema.pan;
    }
    if (isEnabled(bankFormConfig?.cpf?.enabled)) {
      extraFields.cpf = ValidatorSchema.cpf;
    }
    if (isEnabled(bankFormConfig?.curp?.enabled)) {
      extraFields.curp = ValidatorSchema.curp;
    }
    if (isPEN && isEnabled(bankFormConfig?.cci?.enabled)) {
      extraFields.cci = ValidatorSchema.cci;
    }
    if ((isPEN || isCOP) && isEnabled(bankFormConfig?.cardType?.enabled)) {
      // Use server-provided cardTypes or fallback to defaults
      const cardTypeOptions = bankFormConfig?.cardType?.cardTypeOptions ?? DEFAULT_CARD_TYPES;
      extraFields.cardType = z.enum(cardTypeOptions as [string, ...string[]], {
        required_error: "required",
        message: "required",
      });
    }

    if (Object.keys(extraFields).length) {
      common = (common as z.ZodObject<any>).extend(extraFields) as z.ZodTypeAny;
    }

    // PIX key nested discriminated union (BRL only when enabled)
    let pixSchema: z.ZodTypeAny | null = null;
    if (isBRL && isEnabled(bankFormConfig?.pixKey?.enabled)) {
      pixSchema = z.discriminatedUnion(
        "pixKeyType",
        [
          z.object({ pixKeyType: z.literal("cpf"), accountNo: ValidatorSchema.pixKeyCpf }),
          z.object({ pixKeyType: z.literal("email"), accountNo: ValidatorSchema.pixKeyEmail }),
          z.object({
            pixKeyType: z.literal("phone"),
            accountNo: ValidatorSchema.pixKeyPhone({
              minLength: phoneMinLength,
              maxLength: phoneMaxLength,
              startWith: phoneStartWith,
              simpleError: phoneSimpleError,
              msgType: "phone",
            }),
          }),
          z.object({ pixKeyType: z.literal("evp"), accountNo: ValidatorSchema.pixKeyRandom }),
        ],
        {
          errorMap: () => {
            return { message: "required" };
          },
        },
      );
    }

    // PID nested discriminated union (PEN and COP when enabled)
    let pidSchema: z.ZodTypeAny | null = null;
    if ((isPEN || isCOP) && isEnabled(bankFormConfig?.pid?.enabled)) {
      const pidTypeSubSchemas = [
        z.object({ pidType: z.literal("dni"), pidNumber: ValidatorSchema.pid.dni }),
        z.object({ pidType: z.literal("pen_pas"), pidNumber: ValidatorSchema.pid.pen_pas }),
        z.object({ pidType: z.literal("ruc"), pidNumber: ValidatorSchema.pid.ruc }),
        z.object({ pidType: z.literal("cc"), pidNumber: ValidatorSchema.pid.cc }),
        z.object({ pidType: z.literal("nit"), pidNumber: ValidatorSchema.pid.nit }),
        z.object({ pidType: z.literal("cop_pas"), pidNumber: ValidatorSchema.pid.cop_pas }),
      ] as unknown as [z.ZodDiscriminatedUnionOption<"pidType">, ...z.ZodDiscriminatedUnionOption<"pidType">[]];

      // switch CE validations
      if (isPEN) {
        pidTypeSubSchemas.push(z.object({ pidType: z.literal("ce"), pidNumber: ValidatorSchema.pid.pen_ce }));
      }

      // switch COP validations
      if (isCOP) {
        pidTypeSubSchemas.push(z.object({ pidType: z.literal("ce"), pidNumber: ValidatorSchema.pid.cop_ce }));
      }

      pidSchema = z.discriminatedUnion("pidType", pidTypeSubSchemas, {
        errorMap: () => {
          return { message: "required" };
        },
      });
    }

    // Currency-specific account validation
    let accountSchema: z.ZodTypeAny;
    if (isINR) {
      const upiBranch = z.object({
        bankTypeOption: z.literal("upi"),
        accountNo: ValidatorSchema.upiId,
      });

      const bankWithIfsc = z.object({
        bankTypeOption: z.literal("bank"),
        accountNo: ValidatorSchema.payment.bank.accountNoINR,
        ifscChecked: z.literal(true),
        ifsc: ValidatorSchema.payment.bank.ifsc,
      });

      const bankWithoutIfsc = z.object({
        bankTypeOption: z.literal("bank"),
        accountNo: ValidatorSchema.payment.bank.accountNoINR,
        ifscChecked: z.union([z.literal(false), z.undefined()]).optional(),
        ifsc: z.string().optional(),
      });

      accountSchema = z.union([upiBranch, bankWithIfsc, bankWithoutIfsc]);
    } else {
      let accountNoSchema;
      if (isMXN) {
        accountNoSchema = ValidatorSchema.clabeNumber;
      } else if (!!pixSchema) {
        accountNoSchema = z.string().optional();
      } else accountNoSchema = ValidatorSchema.payment.bank.accountNoDefault;

      accountSchema = z.object({
        // bankType not used outside INR; keep optional for compatibility
        bankTypeOption: z.string().optional(),
        accountNo: accountNoSchema,
        ifscChecked: z.boolean().optional(),
        ifsc: z.string().optional(),
      });
    }

    let baseSchema: z.ZodTypeAny = accountSchema.and(common);
    if (pixSchema) baseSchema = baseSchema.and(pixSchema);
    if (pidSchema) baseSchema = baseSchema.and(pidSchema);

    const schemaWithDerive = z
      .preprocess((raw) => {
        const data = raw && typeof raw === "object" ? { ...(raw as any) } : (raw as any);
        if (!data) return raw;

        // 1) Derive normalized bankType regardless of UI
        data.bankType = deriveBankType(currency as Currency, data.bankTypeOption, data.ifscChecked);

        // 2) Derive accountType only when bank selector is hidden
        const nextAccountType = deriveAccountType(currency as Currency, data);
        if (nextAccountType) data.accountType = nextAccountType;

        return data;
      }, baseSchema)
      .refine(
        (data: any) => {
          return data.bankType !== MemberBankFundingType.IFSC || !!getPaymentMethodByKey(data.accountType);
        },
        {
          path: ["ifsc"],
          message: "ifsc code is not valid",
        },
      )
      .refine(
        (data: any) => data.bankType !== MemberBankFundingType.CLABE || !!getPaymentMethodByKey(data.accountType),
        {
          path: ["accountNo"],
          message: "clabe is not valid",
        },
      )
      .refine(
        (data: any) => {
          // Validate CCI is required when bankType is CCI
          if (isPEN && data.bankType === MemberBankFundingType.CCI) {
            return !!data.cci && data.cci.trim().length > 0;
          }
          return true;
        },
        {
          path: ["cci"],
          message: "cci code is required",
        },
      )
      .refine((data: any) => !checkIfFundingProfileExists(data.accountNo, data.accountType), {
        message: isBRL
          ? "pix key already added to payment account"
          : `${MemberSettingFundingMode.BankOnly} already added`,
        path: ["accountNo"],
      });

    return schemaWithDerive;
  }, [
    bankFormConfig,
    nameMode,
    legalName,
    checkIfFundingProfileExists,
    currency,
    phoneMinLength,
    phoneMaxLength,
    phoneStartWith,
    phoneSimpleError,
    deriveBankType,
    deriveAccountType,
    getPaymentMethodByKey,
  ]);

  const form = useForm<FundingProfileFieldValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    criteriaMode: "all",
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountNo: "",
      accountType: "",
      accountDescription: "",
      isDefault: true,
      firstName: "",
      lastName: "",
      accountName: "",
      pan: "",
      bankType: "",
      bankTypeOption: bankFormConfig?.paymentMethodOptions?.[0] ?? undefined,
      ifscChecked: true,
      ifsc: "",
      curp: "",
      cpf: "",
      pidType: "",
      pidNumber: "",
      cci: "",
      cardType: "",
    },
  });

  // Reactive silent derive for bankType and accountType so watch() consumers get updates immediately
  useEffect(() => {
    const subscription = form.watch((values) => {
      const v = values as FundingProfileFieldValues;

      // Normalize PAN to uppercase immediately as the user types
      if (typeof v.pan === "string") {
        const upperPan = v.pan.toUpperCase();
        if (v.pan !== upperPan) {
          form.setValue("pan", upperPan, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }
      }

      // Normalize PID number to uppercase for CE, PEN_PAS, and COP_PAS types
      if (
        typeof v.pidNumber === "string" &&
        (v.pidType === "ce" || v.pidType === "pen_pas" || v.pidType === "cop_pas")
      ) {
        const upperPidNumber = v.pidNumber.toUpperCase();
        if (v.pidNumber !== upperPidNumber) {
          form.setValue("pidNumber", upperPidNumber, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }
      }

      const nextBankType = deriveBankType(currency as Currency, v.bankTypeOption, v.ifscChecked);
      if (v.bankType !== nextBankType) {
        form.setValue("bankType", nextBankType as any, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }

      const nextAccountType = deriveAccountType(currency as Currency, v);

      if (nextAccountType && v.accountType !== nextAccountType) {
        form.setValue("accountType", nextAccountType, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, currency, deriveBankType, deriveAccountType]);

  const pixKeyType$ = form.watch("pixKeyType");
  const pidType$ = form.watch("pidType");

  useEffect(() => {
    if (!!pixKeyType$) {
      form.clearErrors("accountNo");
      form.setValue("accountNo", "", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [pixKeyType$, form]);

  useEffect(() => {
    if (!!pidType$) {
      form.clearErrors("pidNumber");
      form.setValue("pidNumber", "", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [pidType$, form]);

  return { form, schema: formSchema };
};
